// https://github.com/tweenjs/tween.js/pull/619/files
// https://github.com/tweenjs/tween.js/issues/116
export type EasingFunction = (t: number) => number;

export const generateExponential = function (
  // A value of 300 makes the curve very close to Exponential with its value of 1024.
  base: number = 300,
): { In: EasingFunction; Out: EasingFunction; InOut: EasingFunction } {
  // https://www.desmos.com/calculator/pioplwo3zq

  if (base <= 0) {
    console.warn(
      'base should be larger than 0. You might like to try between 20 and 400, maybe more. Setting a default value of 300.',
    );
    base = 300;
  }

  function In(amount: number): number {
    // Start similar to Exponential
    let expo = base ** (amount - 1);

    // adjust so it hits 0 when amount is 0
    expo = expo - expo * (1 - amount);

    return expo;
  }

  function Out(amount: number): number {
    // translate X by 1
    amount = amount + 1;

    // Similar to In, but negate power to make the graph have a negative slope instead of positive.
    let expo = base ** -(amount - 1);

    // adjust so it hits 1 when amount is 1
    expo = expo - expo * -(1 - amount);

    // Flip it upside down, move it up by 1.
    return -expo + 1;
  }

  function InOut(amount: number): number {
    amount *= 2;

    if (amount < 1) return In(amount) / 2;

    // Similar to In, but negate power to make the graph have a negative slope instead of positive.
    let expo = base ** -(amount - 1);

    // adjust so it hits 1 when amount is 1
    expo = expo - expo * -(1 - amount);

    // Flip it upside down, move it up by 2.
    expo = -expo + 2;

    return expo / 2;
  }

  return {
    In,
    Out,
    InOut,
  };
};

export function createCubicBezierEasing(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): EasingFunction {
  // Coefficients for x(t) and y(t)
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;

  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;

  function sampleX(t: number): number {
    return ((ax * t + bx) * t + cx) * t;
  }

  function sampleY(t: number): number {
    return ((ay * t + by) * t + cy) * t;
  }

  function sampleDerivativeX(t: number): number {
    return (3 * ax * t + 2 * bx) * t + cx;
  }

  // Solve x(t) = amount for t, then return y(t).
  function solveT(amount: number): number {
    // Good initial guess
    let t = amount;

    // Newton-Raphson
    for (let i = 0; i < 6; i++) {
      const x = sampleX(t) - amount;
      const d = sampleDerivativeX(t);
      if (Math.abs(x) < 1e-7) return t;
      if (d === 0) break;
      t -= x / d;
    }

    // Fallback: bisection (robust if Newton fails)
    let lo = 0;
    let hi = 1;
    t = amount;

    for (let i = 0; i < 20; i++) {
      const x = sampleX(t);
      if (Math.abs(x - amount) < 1e-7) break;
      if (x < amount) lo = t;
      else hi = t;
      t = (lo + hi) * 0.5;
    }

    return t;
  }

  return function (amount: number): number {
    if (amount <= 0) return 0;
    if (amount >= 1) return 1;
    return sampleY(solveT(amount));
  };
}

export const generateElastic = function (): { Out: EasingFunction } {
  function Out(amount: number): number {
    if (amount === 0) {
      return 0;
    }
    if (amount === 1) {
      return 1;
    }

    const peakValue = 0.8;
    const peakAmount = 1.05;

    const A = (1 - peakValue) / Math.sin(Math.PI * peakAmount);

    return 1 - A * Math.sin(Math.PI * amount);

    /*
    return (
      7.6171875 * amount**5
      - 15.3125 * amount**4
      + 5.7734375 * amount**3
      + 2.921875 * amount**2
    );

     */

    //return Math.pow(2, -16 * amount) * Math.sin((amount - 0.1) * 5 * Math.PI) + 1;
  }

  return {
    Out,
  };
};

export const sequenceEasing = function (
  easeA: EasingFunction,
  easeB: EasingFunction,
  cutoff: number,
): EasingFunction {
  return function (t: number): number {
    if (t <= 0) return 0;
    if (t >= 1) return 1;

    // Phase 1 — 0 to cutoff
    if (t < cutoff) {
      // Normalize to [0, 1]
      const u = t / cutoff;
      // First easing is responsible for 0 → cutoff
      return easeA(u) * cutoff;
    }

    // Phase 2 — cutoff to 1
    // Normalize to [0, 1] for easeB
    const u = (t - cutoff) / (1 - cutoff);
    // easeB runs from cutoff → 1
    return cutoff + easeB(u) * (1 - cutoff);
  };
};

export const smoothSequenceEasingWithCutoffValue = function (
  easeA: EasingFunction,
  easeB: EasingFunction,
  cutoffTime: number, // 0 < cutoffTime < 1  (when we switch)
  cutoffValue: number, // usually 0..1 (can be >1 if you want overshoot)
  blendWidth: number, // size of smooth zone around cutoff (e.g. 0.1)
): EasingFunction {
  // Clamp basic stuff a bit
  cutoffTime = Math.max(0, Math.min(1, cutoffTime));
  blendWidth = Math.max(0, Math.min(1, blendWidth || 0));

  const half = blendWidth * 0.5;
  const blendStart = Math.max(0, cutoffTime - half);
  const blendEnd = Math.min(1, cutoffTime + half);

  return function (t: number): number {
    if (t <= 0) return 0;
    if (t >= 1) return 1;

    // --- Phase A: before blend zone ---
    if (t <= blendStart) {
      // 0..cutoffTime -> 0..1
      const u = t / cutoffTime;
      // map 0..1 -> 0..cutoffValue
      return cutoffValue * easeA(u);
    }

    // --- Phase B: after blend zone ---
    if (t >= blendEnd) {
      // cutoffTime..1 -> 0..1
      const u = (t - cutoffTime) / (1 - cutoffTime);
      // map 0..1 -> cutoffValue..1
      return cutoffValue + (1 - cutoffValue) * easeB(u);
    }

    // --- Blend zone around cutoffTime ---
    // alpha in [0,1] across the blend band
    const alpha = (t - blendStart) / (blendEnd - blendStart);
    // smoothstep for softer blending
    const s = alpha * alpha * (3 - 2 * alpha);

    // A side: evaluated at this t
    const uA = t / cutoffTime; // 0..1-ish
    const yA = cutoffValue * easeA(uA);

    // B side: evaluated at this t
    const uB = (t - cutoffTime) / (1 - cutoffTime); // 0..1-ish
    const yB = cutoffValue + (1 - cutoffValue) * easeB(Math.max(0, Math.min(1, uB)));

    // crossfade between A and B
    return yA * (1 - s) + yB * s;
  };
};
